import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface DriveConnection {
  google_account_email: string;
  status: 'active' | 'expired' | 'disconnected';
  connected_at: string;
  connected_by: string | null;
}

export interface DriveFile {
  id: string;
  google_file_id: string;
  name: string;
  mime_type: string;
  size_bytes: number | null;
  visibility: 'private' | 'team' | 'admin_only';
  uploaded_by: string | null;
  created_at: string;
}

export const driveKeys = {
  all: ['gdrive'] as const,
  connection: (tenantId: string) => [...driveKeys.all, 'connection', tenantId] as const,
  files: (tenantId: string) => [...driveKeys.all, 'files', tenantId] as const,
} as const;

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

export function useDriveConnection(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: driveKeys.connection(tenantId),
    queryFn: () => apiFetch<DriveConnection | null>(supabase, 'gdrive/connection'),
    enabled: Boolean(tenantId),
  });
}

export function useConnectDrive(supabase: SupabaseClientLike, _tenantId: string) {
  return useMutation({
    mutationFn: async () => {
      const result = await apiFetch<{ url: string }>(supabase, 'gdrive/oauth/start');
      window.location.assign(result.url);
    },
  });
}

export function useDisconnectDrive(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(supabase, 'gdrive/connection', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: driveKeys.connection(tenantId) });
      void qc.invalidateQueries({ queryKey: driveKeys.files(tenantId) });
    },
  });
}

export function useDriveFiles(
  supabase: SupabaseClientLike,
  tenantId: string,
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: driveKeys.files(tenantId),
    queryFn: () => apiFetch<{ data: DriveFile[] }>(supabase, 'gdrive/files').then((r) => r.data),
    enabled: Boolean(tenantId) && (opts.enabled ?? true),
  });
}

export function useUploadFile(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const headers = await getAuthHeaders(supabase);
      const res = await fetch('/functions/v1/gdrive/upload', {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
          error?: { message?: string };
        };
        throw new Error(body.error?.message ?? res.statusText);
      }
      return res.json() as Promise<DriveFile>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: driveKeys.files(tenantId) });
    },
  });
}

export function useDeleteDriveFile(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) =>
      apiFetch<{ ok: true }>(supabase, `gdrive/files/${fileId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: driveKeys.files(tenantId) });
    },
  });
}

export function useDriveProxyUrl(googleFileId: string): string {
  return `/functions/v1/gdrive/proxy/${googleFileId}`;
}
